import { useEffect, useMemo, useState } from "react";
import { ArrowRight, CheckCircle2, Gift, Send, Share2, UserRound, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api";
import { fillTemplate, useAdvisorContent } from "@/hooks";
import { readAdvisorState } from "@/data/polyFitData";
import { trackAdvisorEvent } from "@/utils/analytics";

const blankContact = { name: "", email: "", organization: "", designation: "", mobile_phone: "", email_consent: false, whatsapp_consent: false, phone_consent: false };
const consentText = {
  email_consent: "HP may contact me with personalised offers, support updates, and event news by email",
  whatsapp_consent: "HP may contact me with personalised offers, support updates, and event news by WhatsApp",
  phone_consent: "HP may contact me with personalised offers, support updates, and event news by phone",
};

export function getStoredContact() {
  try { return JSON.parse(window.localStorage.getItem("polyFitContact") || "null"); } catch { return null; }
}

export function isContactConfirmed() {
  return window.sessionStorage.getItem("polyFitContactSession") === readAdvisorState().sessionId;
}

function markContactConfirmed(sessionId) {
  window.sessionStorage.setItem("polyFitContactSession", sessionId);
}

function firstName(contact) {
  return (contact?.name || "").trim().split(/\s+/)[0] || "";
}

const consentKeys = ["email_consent", "whatsapp_consent", "phone_consent"];

const fieldDefs = [
  ["name", "Name", "text", "Jane Appleseed"],
  ["email", "Work email", "email", "jane@company.com"],
  ["organization", "Organization", "text", "Company name"],
  ["designation", "Designation", "text", "Your role"],
  ["mobile_phone", "Mobile phone", "tel", "+91 98765 43210"],
];

function ContactFields({ form, update, prefix, includeVendor = false, vendorLabel }) {
  return (
    <div className="field-grid" data-testid={`${prefix}-fields`}>
      {fieldDefs.map(([name, label, type, placeholder]) => (
        <label className={`field ${form[name] ? "filled" : ""}`} key={name} data-testid={`${prefix}-${name.replace("_", "-")}-label`}>
          <span>{label}</span>
          <input name={name} type={type} value={form[name]} onChange={update} placeholder={placeholder} required data-testid={`${prefix}-${name.replace("_", "-")}-input`} />
        </label>
      ))}
      {includeVendor && (
        <label className="field filled field-wide" data-testid={`${prefix}-current-headset-label`}>
          <span>{vendorLabel}</span>
          <select name="current_headset_vendor" value={form.current_headset_vendor || "Poly"} onChange={update} required data-testid={`${prefix}-current-headset-select`}><option>Poly</option><option>Jabra</option><option>Schneider</option><option>Logitech</option><option>Others</option></select>
        </label>
      )}
    </div>
  );
}

function ConsentChecklist({ form, update, prefix, copy, attention = false, error = "" }) {
  const [before, after = ""] = (copy.intro || "").split("Privacy & Terms");
  const missing = consentKeys.filter((key) => !form[key]).length;
  return (
    <div className={`consent-group ${attention ? "attention" : ""}`} data-consent-group data-testid={`${prefix}-privacy-checklist`}>
      <p className="consent-heading" data-testid={`${prefix}-privacy-heading`}><span className="consent-required" data-testid={`${prefix}-consent-required-badge`}>Required · {missing ? `${missing} of 3 left` : "all set"}</span>{before}<a href="/privacy-terms" target="_blank" rel="noreferrer" data-testid={`${prefix}-privacy-link`}>Privacy &amp; Terms</a>{after}</p>
      <ul className="consent-list">
        {Object.entries(consentText).map(([key, label]) => (
          <li key={key}>
            <label className="consent-row" data-testid={`${prefix}-${key}-label`}>
              <span>{label}</span>
              <span className="switch"><input type="checkbox" name={key} checked={!!form[key]} onChange={update} data-testid={`${prefix}-${key}-checkbox`} /><i /></span>
            </label>
          </li>
        ))}
      </ul>
      {error ? <p className="form-error consent-error" role="alert" data-testid={`${prefix}-error-alert`}>{error}</p> : <small className="consent-note">{copy.note}</small>}
    </div>
  );
}

function useConsentAttention() {
  const [attention, setAttention] = useState(false);
  const draw = () => {
    setAttention(true);
    window.setTimeout(() => setAttention(false), 1400);
    window.requestAnimationFrame(() => document.querySelector("[data-consent-group]")?.scrollIntoView({ behavior: "smooth", block: "center" }));
    toast.error("Please switch on all three permissions to continue.");
  };
  return [attention, draw];
}

function useSheetClose(onDone) {
  const [closing, setClosing] = useState(false);
  const close = () => { setClosing(true); window.setTimeout(() => { setClosing(false); onDone?.(); }, 260); };
  return [closing, close];
}

export function PreQuizContactModal({ open, onComplete }) {
  const { copy } = useAdvisorContent();
  const saved = useMemo(() => getStoredContact(), []);
  const [form, setForm] = useState(() => saved || blankContact);
  const [useSaved, setUseSaved] = useState(() => Boolean(saved?.name && saved?.email && saved?.email_consent && saved?.whatsapp_consent && saved?.phone_consent));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [closing, close] = useSheetClose(onComplete);
  const [attention, drawAttention] = useConsentAttention();
  useEffect(() => {
    if (open) trackAdvisorEvent("lead_capture_opened", { sessionId: readAdvisorState().sessionId, source: "pre_quiz_contact" });
  }, [open]);
  if (!open) return null;
  const update = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((old) => ({ ...old, [name]: type === "checkbox" ? checked : value }));
  };
  const submit = async (event) => {
    event.preventDefault();
    setError("");
    if (!form.email_consent || !form.whatsapp_consent || !form.phone_consent) { setError("Please switch on all three permissions to start."); drawAttention(); return; }
    setSubmitting(true);
    const payload = { ...form, session_id: readAdvisorState().sessionId };
    try { await api.submitAdvisorContact(payload); } catch (err) { setError(err?.response?.data?.detail || "Could not save your details."); setSubmitting(false); return; }
    window.localStorage.setItem("polyFitContact", JSON.stringify(payload));
    markContactConfirmed(payload.session_id);
    trackAdvisorEvent("lead_submitted", { sessionId: payload.session_id, source: "pre_quiz_contact" });
    setSubmitting(false);
    close();
  };
  const continueAsSaved = async () => {
    setError("");
    setSubmitting(true);
    const payload = { ...blankContact, ...saved, session_id: readAdvisorState().sessionId };
    try { await api.submitAdvisorContact(payload); } catch (err) { setError(err?.response?.data?.detail || "Could not reuse your details."); setSubmitting(false); return; }
    markContactConfirmed(payload.session_id);
    trackAdvisorEvent("lead_submitted", { sessionId: payload.session_id, source: "pre_quiz_returning" });
    setSubmitting(false);
    close();
  };
  if (useSaved) {
    return (
      <div className={`sheet-backdrop ${closing ? "closing" : ""}`} role="presentation" data-testid="prequiz-contact-modal-backdrop">
        <section className="sheet sheet-compact" role="dialog" aria-modal="true" aria-labelledby="prequiz-title" data-testid="prequiz-contact-modal">
          <span className="sheet-grabber" aria-hidden="true" />
          <div className="sheet-form" data-testid="prequiz-returning-panel">
            <header className="sheet-head" data-testid="prequiz-contact-intro">
              <span className="sheet-pill" data-testid="prequiz-contact-eyebrow"><Gift size={14} /> {copy.returning.eyebrow}</span>
              <h2 id="prequiz-title" data-testid="prequiz-contact-title">{fillTemplate(copy.returning.title, { first: firstName(saved) })}</h2>
              <p data-testid="prequiz-contact-subtitle">{copy.returning.subtitle}</p>
            </header>
            <div className="sheet-body">
              <div className="saved-contact" data-testid="prequiz-saved-contact">
                <span className="saved-avatar"><UserRound size={20} /></span>
                <div><b data-testid="prequiz-saved-name">{saved.name}</b><small data-testid="prequiz-saved-email">{saved.email}{saved.organization ? ` · ${saved.organization}` : ""}</small></div>
              </div>
              {error && <p className="form-error" data-testid="prequiz-contact-error-alert">{error}</p>}
            </div>
            <footer className="sheet-footer">
              <button className="primary-cta pill-cta sheet-submit" type="button" onClick={continueAsSaved} disabled={submitting} data-testid="prequiz-continue-as-button">{submitting ? "One moment…" : fillTemplate(copy.returning.continue_label, { first: firstName(saved) })} <ArrowRight size={16} /></button>
              <button className="text-btn" type="button" onClick={() => { setForm({ ...blankContact, ...saved, email_consent: false, whatsapp_consent: false, phone_consent: false }); setUseSaved(false); }} data-testid="prequiz-use-different-button">{fillTemplate(copy.returning.switch_label, { first: firstName(saved) })}</button>
            </footer>
          </div>
        </section>
      </div>
    );
  }
  return (
    <div className={`sheet-backdrop ${closing ? "closing" : ""}`} role="presentation" data-testid="prequiz-contact-modal-backdrop">
      <section className="sheet" role="dialog" aria-modal="true" aria-labelledby="prequiz-title" data-testid="prequiz-contact-modal">
        <span className="sheet-grabber" aria-hidden="true" />
        <form className="sheet-form" onSubmit={submit} data-testid="prequiz-contact-form">
          <header className="sheet-head" data-testid="prequiz-contact-intro">
            <span className="sheet-pill" data-testid="prequiz-contact-eyebrow"><Gift size={14} /> {copy.prequiz.eyebrow}</span>
            <h2 id="prequiz-title" data-testid="prequiz-contact-title">{copy.prequiz.title}</h2>
            <p data-testid="prequiz-contact-subtitle">{copy.prequiz.subtitle}</p>
          </header>
          <div className="sheet-body">
            <ContactFields form={form} update={update} prefix="prequiz-contact" />
            <ConsentChecklist form={form} update={update} prefix="prequiz-contact" copy={copy.consent} attention={attention} error={error} />
          </div>
          <footer className="sheet-footer">
            <button className="primary-cta pill-cta sheet-submit" type="submit" disabled={submitting} data-testid="prequiz-contact-submit-button">{submitting ? "Submitting…" : copy.prequiz.submit_label} <Send size={16} /></button>
            <p className="sheet-assurance" data-testid="prequiz-contact-assurance"><CheckCircle2 size={14} /> {copy.prequiz.assurance}</p>
          </footer>
        </form>
      </section>
    </div>
  );
}

export function DemoOfferModal({ open, onClose, product, advisorState }) {
  const { copy } = useAdvisorContent();
  const initial = useMemo(() => ({ ...blankContact, ...(getStoredContact() || {}), current_headset_vendor: "Poly" }), []);
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [closing, close] = useSheetClose(onClose);
  const [attention, drawAttention] = useConsentAttention();
  if (!open || !product) return null;
  const update = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((old) => ({ ...old, [name]: type === "checkbox" ? checked : value }));
  };
  const submit = async (event) => {
    event.preventDefault();
    setError("");
    if (!form.email_consent || !form.whatsapp_consent || !form.phone_consent) { setError("Please switch on all three permissions to continue."); drawAttention(); return; }
    setSubmitting(true);
    try {
      await api.submitAdvisorDemoOffer({ ...form, result_product_id: product.id, result_product_name: product.brandName, answers: advisorState.answerLabels || {}, path: advisorState.path || [], session_id: advisorState.sessionId });
      toast.success("Your demo and offer request has been captured.");
      trackAdvisorEvent("lead_submitted", { sessionId: advisorState.sessionId, result_product_id: product.id, source: "demo_offer" });
      close();
    } catch (err) { setError(err?.response?.data?.detail || "Could not submit your request."); }
    finally { setSubmitting(false); }
  };
  return (
    <div className={`sheet-backdrop ${closing ? "closing" : ""}`} role="presentation" data-testid="demo-offer-modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) close(); }}>
      <section className="sheet" role="dialog" aria-modal="true" aria-labelledby="demo-offer-title" data-testid="demo-offer-modal">
        <span className="sheet-grabber" aria-hidden="true" />
        <button className="sheet-close" type="button" onClick={close} aria-label="Close demo offer form" data-testid="demo-offer-close-button"><X size={16} /></button>
        <form className="sheet-form" onSubmit={submit} data-testid="demo-offer-form">
          <header className="sheet-head" data-testid="demo-offer-intro">
            <span className="sheet-pill" data-testid="demo-offer-eyebrow"><Gift size={14} /> {copy.demo.eyebrow}</span>
            <h2 id="demo-offer-title" data-testid="demo-offer-title">{copy.demo.title}</h2>
            <p data-testid="demo-offer-summary">{copy.demo.subtitle}</p>
            <div className="sheet-product" data-testid="demo-offer-product-summary"><CheckCircle2 size={15} /> {product.brandName}</div>
          </header>
          <div className="sheet-body">
            <ContactFields form={form} update={update} prefix="demo-offer" includeVendor vendorLabel={copy.demo.vendor_label} />
            <ConsentChecklist form={form} update={update} prefix="demo-offer" copy={copy.consent} attention={attention} error={error} />
          </div>
          <footer className="sheet-footer">
            <button className="primary-cta pill-cta sheet-submit" type="submit" disabled={submitting} data-testid="demo-offer-submit-button">{submitting ? "Submitting…" : copy.demo.submit_label} <Share2 size={16} /></button>
          </footer>
        </form>
      </section>
    </div>
  );
}